import React, { useEffect, useState } from "react";
import { AlertTriangle, FileText, PhoneCall, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATES = [
  "NEW_APPLICATION", "VYTALI_PROCESSING", "VYTALI_REVIEWED", "READY_FOR_LEA",
  "LEA_CONTACTED", "QUALIFICATION_COMPLETE", "INTERVIEW_SCHEDULED",
  "INTERVIEW_COMPLETED", "ORIENTATION_PENDING", "TRAINING_ELIGIBLE", "CLOSED",
];

function List({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <p className="cs-kicker text-muted-foreground">{label}</p>
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((item, index) => (
          <li key={index} className="leading-relaxed">· {typeof item === "string" ? item : JSON.stringify(item)}</li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminRecruitment() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [state, setState] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/recruit/applications", { params: state ? { state } : {} })
      .then((res) => setItems(res.data.items))
      .finally(() => setLoading(false));
  };

  useEffect(load, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/admin/recruit/funnel").then((res) => setFunnel(res.data)).catch(() => setFunnel(null));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setDetail(null);
    api.get(`/admin/recruit/applications/${selected}`).then((res) => setDetail(res.data));
  }, [selected]);

  const markReady = async () => {
    try {
      const res = await api.post(`/admin/recruit/applications/${selected}/ready-for-lea`);
      toast.success(`READY_FOR_LEA · Léa ${res.data.lea_connection_status}`);
      const fresh = await api.get(`/admin/recruit/applications/${selected}`);
      setDetail(fresh.data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.msg || "Action refusée");
    }
  };

  const intelligence = detail?.intelligence?.intelligence;
  const brief = detail?.intelligence?.lea_call_brief;

  return (
    <div data-testid="admin-recruitment" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-light">{t("admin.recruitment")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Candidatures issues de la landing page de recrutement. Intelligence Vytali produite dans Sparks
          (suggestions seulement). Léa et Calendar ne sont pas connectés : aucun appel n'est déclenché.
        </p>
      </div>

      {funnel && (
        <div data-testid="admin-recruitment-funnel" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {funnel.events.slice(0, 8).map((row) => (
            <div key={row.event} className="border border-border bg-surface p-4">
              <p className="text-xl font-light">{row.count}</p>
              <p className="mt-1 text-xs text-muted-foreground">{row.event}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="admin-recruitment-filter-all"
          onClick={() => setState("")}
          className={`rounded-full border px-3 py-1 text-xs ${state === "" ? "border-accent bg-accent text-accent-foreground" : "border-border"}`}
        >
          Tous
        </button>
        {STATES.map((key) => (
          <button
            key={key}
            type="button"
            data-testid={`admin-recruitment-filter-${key}`}
            onClick={() => setState(key)}
            className={`rounded-full border px-3 py-1 text-xs ${state === key ? "border-accent bg-accent text-accent-foreground" : "border-border"}`}
          >
            {key}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-muted-foreground">…</p>}

      {!loading && items.length === 0 && (
        <p data-testid="admin-recruitment-empty" className="border border-border bg-surface p-8 text-sm text-muted-foreground">
          Aucune candidature de recrutement pour le moment.
        </p>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <ul data-testid="admin-recruitment-list" className="divide-y divide-border border border-border bg-surface">
              {items.map((row) => (
                <li key={row.application_id}>
                  <button
                    type="button"
                    data-testid={`admin-recruitment-row-${row.application_id}`}
                    onClick={() => setSelected(row.application_id)}
                    className={`flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted ${
                      selected === row.application_id ? "bg-secondary" : ""
                    }`}
                  >
                    <span className="flex items-center gap-2 text-sm">
                      {row.first_name} {row.last_name}
                      {row.has_resume && <FileText className="h-3.5 w-3.5 text-accent" />}
                      {row.needs_human_review && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                      <span className="ml-auto text-[11px] uppercase tracking-wider text-muted-foreground">
                        {row.recruitment_state}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {row.city}, {row.province_state} · {row.attribution?.utm_source || "direct"}
                      {row.attribution?.utm_campaign ? ` / ${row.attribution.utm_campaign}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            {!selected && (
              <p className="border border-border bg-surface p-8 text-sm text-muted-foreground">
                Sélectionnez une candidature.
              </p>
            )}
            {selected && !detail && <p className="text-sm text-muted-foreground">…</p>}
            {detail && (
              <div data-testid="admin-recruitment-detail" className="space-y-5">
                <div className="border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">Candidat</p>
                  <p className="mt-2 text-base">
                    {detail.candidate.first_name} {detail.candidate.last_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {detail.candidate.email} · {detail.candidate.phone}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {detail.candidate.city}, {detail.candidate.province_state}, {detail.candidate.country}
                  </p>
                  <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
                    <div><dt className="text-xs text-muted-foreground">Intérêts</dt><dd>{(detail.application.interest_tracks || []).join(", ") || "—"}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Expérience vente</dt><dd>{detail.application.sales_experience_level || "—"}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Forces</dt><dd>{(detail.application.strengths || []).join(", ") || "—"}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Langues</dt><dd>{(detail.application.languages || []).join(", ") || "—"}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">Disponibilité</dt><dd>{detail.application.availability || "—"}</dd></div>
                    <div><dt className="text-xs text-muted-foreground">État</dt><dd data-testid="admin-recruitment-state">{detail.application.recruitment_state}</dd></div>
                  </dl>
                  <p className="mt-4 text-sm"><span className="text-xs text-muted-foreground">Objectif : </span>{detail.application.candidate_goal || "—"}</p>
                </div>

                <div className="border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">Provenance</p>
                  <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    {Object.entries(detail.application.attribution || {}).filter(([, v]) => v).map(([k, v]) => (
                      <p key={k}><span className="text-xs text-muted-foreground">{k} : </span>{String(v)}</p>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">
                    Consentement communication : {detail.application.communication_consent ? "oui" : "non"} ·
                    version {detail.application.consent_version} · {detail.application.communication_consent_at}
                  </p>
                  {detail.application.needs_human_review && (
                    <p className="mt-3 text-xs text-destructive">
                      Révision humaine requise : le courriel et le téléphone pointent vers deux candidats différents. Aucune fusion automatique.
                    </p>
                  )}
                </div>

                <div className="border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">CV</p>
                  {detail.documents.length === 0 && <p className="mt-2 text-sm text-muted-foreground">Aucun CV transmis (facultatif).</p>}
                  {detail.documents.map((doc) => (
                    <p key={doc.id} className="mt-2 text-sm">
                      v{doc.version} · {doc.original_filename} · {Math.round((doc.size_bytes || 0) / 1024)} Ko · {doc.provenance}
                    </p>
                  ))}
                </div>

                <div className="border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">
                    <Sparkles className="mr-1 inline h-3.5 w-3.5 text-accent" />
                    Vytali Candidate Intelligence
                  </p>
                  {!intelligence && <p className="mt-2 text-sm text-muted-foreground">Analyse en cours ou non disponible.</p>}
                  {intelligence && (
                    <>
                      <p className="mt-3 text-sm leading-relaxed">{intelligence.candidate_summary || "—"}</p>
                      <List label="Signaux de force" items={intelligence.strength_signals} />
                      <List label="Informations manquantes" items={intelligence.information_gaps} />
                      <List label="Questions à clarifier" items={intelligence.questions_to_clarify} />
                      <List label="Catégories suggérées (exploration seulement)" items={intelligence.suggested_opportunity_categories} />
                      <p className="mt-4 text-xs text-muted-foreground">
                        {detail.intelligence.provenance_note} · VytaliLeads/Vytali : {detail.intelligence.vytali_sync?.status}
                      </p>
                    </>
                  )}
                </div>

                <div className="border border-border bg-surface p-5">
                  <p className="cs-kicker text-muted-foreground">
                    <PhoneCall className="mr-1 inline h-3.5 w-3.5 text-accent" />
                    Préparation d'appel Léa
                  </p>
                  {!brief && <p className="mt-2 text-sm text-muted-foreground">Non disponible.</p>}
                  {brief && (
                    <div className="mt-2 space-y-1 text-sm">
                      <p><span className="text-xs text-muted-foreground">Intérêt principal : </span>{brief.primary_interest || "—"}</p>
                      <p><span className="text-xs text-muted-foreground">Objectif de l'appel : </span>{brief.recommended_call_objective}</p>
                      <p><span className="text-xs text-muted-foreground">Langue : </span>{brief.language}</p>
                      <p><span className="text-xs text-muted-foreground">Identité à divulguer : </span>{brief.assistant_identity}</p>
                    </div>
                  )}
                  <p className="mt-4 text-xs text-muted-foreground">
                    Léa : {detail.application.lea?.status} · Calendar : {detail.application.interview?.status}.
                    Aucun appel n'est déclenché depuis cet écran.
                  </p>
                  <Button
                    data-testid="admin-recruitment-ready-lea"
                    onClick={markReady}
                    disabled={detail.application.recruitment_state === "READY_FOR_LEA"}
                    className="mt-4 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    Marquer prêt pour Léa
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
